import { Text, Title } from '@/shared/ui';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { MeterView } from '@/shared/utils/plan-tab-model';
import { MeterCard } from './MeterCard';

interface Props {
  meters: MeterView[];
  /** Already-formatted renewal date, or empty when unknown. */
  renewsOn: string;
  compact?: boolean;
  /**
   * What the plan gives once the free period is over, for the trial legend
   * ("no Rubi: 800 por mês"). Absent when the catalogue cannot say.
   */
  trialPlan?: TrialPlan | null;
}

/** The plan the trial is a preview of, and its monthly allowances. */
export interface TrialPlan {
  name: string;
  quotas: Partial<Record<MeterView['key'], number | null>>;
}

/**
 * The legend under a trial meter (RN-06): the ceiling is the trial's, so the
 * plan's own allowance is named beside it rather than replacing it.
 *
 * @param meter - The meter.
 * @param trialPlan - The plan the trial previews, when known.
 * @param t - Translation function.
 * @returns The legend, or undefined outside the trial.
 */
function trialLegendOf(
  meter: MeterView, trialPlan: TrialPlan | null | undefined, t: TFunction,
): string | undefined {
  if (!meter.trial) return undefined;
  const quota = trialPlan?.quotas[meter.key];
  return trialPlan && quota
    ? t('subscription.plan.usage.trialLegend', { plan: trialPlan.name, quota })
    : t('subscription.plan.usage.trialLegendNoPlan');
}

/**
 * The tab's main content: what this cycle has left.
 *
 * With no readable usage the section states that plainly rather than drawing
 * zeroed bars — a bar against an unknown denominator reads as a limit someone
 * can hit.
 *
 * @param props - Component props.
 * @returns The usage section.
 */
export function UsageMeters({
  meters, renewsOn, compact = false, trialPlan = null,
}: Props) {
  const { t } = useTranslation('features/pricing');

  return (
    <div className="flex flex-col gap-(--space-3)" data-testid="usage-meters">
      <div className="flex items-baseline justify-between gap-(--space-3) flex-wrap">
        <Title order={2} style={{
          margin: 0, fontSize: 'var(--text-app-section)',
          fontWeight: 'var(--font-bold)', color: 'var(--text-primary)',
        }}>
          {t('subscription.plan.usage.title')}
        </Title>
        {renewsOn && (
          <Text component="span" style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary-soft)' }}>
            {t('subscription.plan.usage.renewsOn', { date: renewsOn })}
          </Text>
        )}
      </div>

      {meters.length === 0 ? (
        <Text component="p" data-testid="usage-unavailable" style={{
          margin: 0, fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)',
        }}>
          {t('subscription.plan.usage.unavailable')}
        </Text>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: compact
            ? 'minmax(0, 1fr)'
            : 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 'var(--space-3)',
        }}>
          {meters.map((meter) => (
            <MeterCard
              key={meter.key}
              meter={meter}
              compact={compact}
              trialLegend={trialLegendOf(meter, trialPlan, t)}
              label={t(`subscription.plan.usage.${meter.key}`)}
              unit={t('subscription.plan.usage.remaining', { count: meter.remaining })}
              usedOf={t('subscription.plan.usage.usedOf', {
                used: meter.used, total: meter.total,
              })}
            />
          ))}
        </div>
      )}
    </div>
  );
}
